import CurrencyInput from "components/form/currency-input";
import { IconTrash } from "@tabler/icons-react";
import { useInvoiceHistory } from "contexts/v1/pages/InvoiceHistoryContext";
import { useEffect, useState } from "react";
import { Offcanvas, Tab, Tabs } from "react-bootstrap";
import Select from "react-select";
import { deleteRequest, postRequest } from "services/api";
import { formatDateToString } from "utils/dateHelpers";
import { formatPrice } from "utils/utils";

const Transaction = ({ transactionCanvas, setTransactionCanvas }) => {
    const {  refetch } = useInvoiceHistory();
    const [transactions, setTransactions] = useState({
        form: {
            transaction_date: formatDateToString(new Date()),
            payment_method: '',
            amount: '',
            note: '',
        },
        data: [],
        message: null,
        error: null,
        loading: false,
        isSubmitting: false,
        tab: 'list',
    });

    useEffect(() => {
        if (transactionCanvas.data) {
            setTransactions({
                form: {
                    transaction_date: formatDateToString(new Date()),
                    payment_method: '',
                    amount: '',
                    note: '',
                },
                data: [],
                message: null,
                error: null,
                loading: false,
                isSubmitting: false,
                tab: 'list',
            });
        }
    }, [transactionCanvas.show]);


    const createTransaction = async (e) => {
        e.preventDefault();
        setTransactions((prev) => ({
            ...prev,
            loading: true,
            isSubmitting: true,
        }));

        try {
            const response = await postRequest(`/invoice/${transactionCanvas.data.invoice_number}/transaction`, {
                ...transactions.form,
                payment_method: transactions.form.payment_method.value,
            });
            if (response.success) {
                refetch().then((response) => {
                    if (response.success) {
                        const updatedTransactions = response.data.find((invoice) => invoice.invoice_number === transactionCanvas.data.invoice_number);
                        setTransactionCanvas((prevState) => ({
                            ...prevState,
                            data: updatedTransactions,
                        }));
                    }
                });

                setTransactions((prev) => ({
                    ...prev,
                    form: {
                        transaction_date: formatDateToString(new Date()),
                        payment_method: '',
                        amount: '',
                        note: '',
                    },
                    message: response.message,
                    error: null,
                    tab: 'list',
                }));
            }
        } catch (error) {
            setTransactions((prev) => ({
                ...prev,
                error: 'Failed to create transaction'
            }));
        } finally {
            setTransactions((prev) => ({
                ...prev,
                loading: false,
                isSubmitting: false,
            }));
        }
    };

    const deleteTransaction = async (id) => {
        setTransactions((prev) => ({
            ...prev,
            loading: true,
        }));

        try {
            const response = await deleteRequest(`/invoice/${transactionCanvas.data.invoice_number}/transaction/${id}`);
            if (response.success) {
                refetch().then((response) => {
                    if (response.success) {
                        const updatedTransactions = response.data.find((invoice) => invoice.invoice_number === transactionCanvas.data.invoice_number);
                        setTransactionCanvas((prevState) => ({
                            ...prevState,
                            data: updatedTransactions,
                        }));
                    }
                });

                setTransactions((prev) => ({
                    ...prev,
                    message: response.message,
                    error: null
                }));
            }
        } catch (error) {
            setTransactions((prev) => ({
                ...prev,
                error: 'Failed to delete transaction'
            }));
        } finally {
            setTransactions((prev) => ({
                ...prev,
                loading: false
            }));
        }
    };

    return (
        <Offcanvas show={transactionCanvas.show} onHide={() => {
            setTransactionCanvas((prevState) => ({ ...prevState, show: false }));
        }} placement='end' style={{ width: '40%' }}>
            <Offcanvas.Header className="border-bottom-0 pb-0" closeButton>
                <Offcanvas.Title>Transaction</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="pt-3">
                <Tabs
                    defaultActiveKey={transactions.tab}
                    onSelect={(k) => setTransactions((prev) => ({ ...prev, tab: k }))}
                    className="mb-3 text-blue"
                    variant="underline"
                    activeKey={transactions.tab}
                >
                    <Tab eventKey="list" title="History" active={transactions.tab === "list" ? true : false}>
                        {transactions.message && (
                            <div className="alert alert-success" role="alert">
                                {transactions.message}
                            </div>
                        )}
                        {/* Table (transaction date, payment method, amount, note) */}
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Payment Method</th>
                                    <th>Amount</th>
                                    <th>Note</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.loading ? (
                                    <tr>
                                        <td colSpan="5" className="text-center">Loading...</td>
                                    </tr>
                                ) : (
                                    transactionCanvas.data && Array.isArray(transactionCanvas.data.transactions) && transactionCanvas.data.transactions.length > 0 ? (
                                        transactionCanvas.data.transactions.map((transaction, index) => (
                                            <tr key={index}>
                                                <td>{transaction.date}</td>
                                                <td>{transaction.payment_method}</td>
                                                <td>{formatPrice(transaction.amount)}</td>
                                                <td>{transaction.note}</td>
                                                <td>
                                                    <button className="btn btn-sm btn-danger" onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteTransaction(transaction.id);
                                                    }}>
                                                        <IconTrash size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center">No data available</td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </Tab>
                    <Tab eventKey="create" title="Create"
                        disabled={(transactionCanvas.data && Number(transactionCanvas.data.balance) === 0)}
                        active={transactions.tab === "create" ? true : false}>
                        {transactions.error && (
                            <div className="alert alert-danger" role="alert">
                                {transactions.error}
                            </div>
                        )}
                        {/* Form Create Transaction */}
                        <form onSubmit={createTransaction}>
                            {/* Transaction Date */}
                            <div className="mb-3">
                                <label htmlFor="transactionDate" className="form-label">Transaction Date</label>
                                <input type="date" className="form-control" id="transactionDate"
                                    value={transactions.form.transaction_date}
                                    onChange={(e) => setTransactions((prev) => ({
                                        ...prev,
                                        form: {
                                            ...prev.form,
                                            transaction_date: e.target.value,
                                        },
                                    }))}
                                    disabled={transactions.isSubmitting} />
                            </div>
                            <div className="mb-3">
                                <label htmlFor="paymentMethod" className="form-label">Payment Method</label>
                                <Select
                                    options={[
                                        { value: 'Bank Transfer', label: 'Bank Transfer' },
                                        { value: 'Cash', label: 'Cash' },
                                    ]}
                                    value={transactions.form.payment_method}
                                    onChange={(e) => setTransactions((prev) => ({
                                        ...prev,
                                        form: {
                                            ...prev.form,
                                            payment_method: e,
                                            amount: transactionCanvas.data.balance,
                                        },
                                    }))}
                                    disabled={transactions.isSubmitting} />
                            </div>
                            <div className="mb-3">
                                <label htmlFor="amount" className="form-label">Amount</label>
                                {/* <input type="number" className="form-control" id="amount"
                                    value={transactions.form.amount}
                                    onChange={(e) => setTransactions((prev) => ({
                                        ...prev,
                                        form: {
                                            ...prev.form,
                                            amount: e.target.value,
                                        },
                                    }))}
                                    disabled={transactions.isSubmitting} /> */}
                                <CurrencyInput
                                    className="form-control"
                                    id="amount"
                                    value={transactions.form.amount}
                                    onChange={(e, rawValue) => setTransactions((prev) => ({
                                        ...prev,
                                        form: {
                                            ...prev.form,
                                            amount: rawValue,
                                        },
                                    }))}
                                    disabled={transactions.isSubmitting}
                                />
                            </div>
                            <div className="mb-3">
                                <label htmlFor="note" className="form-label">Note</label>
                                <textarea className="form-control" id="note" rows="3"
                                    value={transactions.form.note}
                                    onChange={(e) => setTransactions((prev) => ({
                                        ...prev,
                                        form: {
                                            ...prev.form,
                                            note: e.target.value,
                                        },
                                    }))}
                                    disabled={transactions.isSubmitting}
                                ></textarea>
                            </div>
                            <button type="submit" className="btn btn-primary" disabled={transactions.isSubmitting}>Submit</button>
                        </form>
                    </Tab>
                </Tabs>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default Transaction;